import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allArticleContent } from '@/mocks/articleContent';

export default function ArticlePage() {
  const { slug } = useParams();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!slug || !allArticleContent[slug]) {
    return <Navigate to="/" replace />;
  }

  const article = allArticleContent[slug];

  const faqSections = article.sections.filter((s) => s.isFaq);
  const faqSchema = faqSections.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSections.flatMap((s) =>
      (s.faqs || []).map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      }))
    ),
  } : null;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: 'ACBuySpreadsheet',
    },
    publisher: {
      '@type': 'Organization',
      name: 'ACBuySpreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: `${window.location.origin}/favicon.jpeg`,
      },
    },
    description: article.intro.substring(0, 160),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${window.location.origin}/article/${article.slug}`,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24 pb-16">
        {faqSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />

        <article className="max-w-[720px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm mb-8 font-label" aria-label="Breadcrumb">
            <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors">
              Home
            </Link>
            <span className="text-foreground-400">/</span>
            <span className="text-foreground-800 font-medium truncate max-w-[300px]">
              {article.label}
            </span>
          </nav>

          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-[1.15] mb-4">
            {article.title}
          </h1>

          <p className="text-foreground-500 text-sm mb-10 font-label">{article.date}</p>

          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full aspect-[16/9] object-cover"
              loading="eager"
            />
          </div>

          <div className="prose-custom">
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-8 font-body">
              {article.intro}
            </p>

            {article.sections.map((section, i) => {
              if (section.isFaq) {
                return (
                  <section key={i} className="mb-10">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                      {section.heading}
                    </h2>
                    <div className="space-y-3">
                      {(section.faqs || []).map((faq, j) => (
                        <details key={j} className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden">
                          <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-label text-foreground-800 text-sm font-semibold list-none">
                            {faq.q}
                            <i className="ri-add-line text-lg text-foreground-400 group-open:hidden"></i>
                            <i className="ri-subtract-line text-lg text-foreground-400 hidden group-open:block"></i>
                          </summary>
                          <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed font-body">
                            {faq.a}
                          </div>
                        </details>
                      ))}
                    </div>
                  </section>
                );
              }

              return (
                <section key={i} className="mb-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  {section.content.split('\n\n').map((para, j) => (
                    <p key={j} className="text-foreground-700 text-base leading-relaxed mb-4 font-body">
                      {para}
                    </p>
                  ))}
                </section>
              );
            })}

            <section className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Cost Comparison Table
              </h2>
              <div className="overflow-x-auto rounded-xl border border-background-200/70">
                <table className="w-full text-sm font-label">
                  <thead>
                    <tr className="bg-background-100 text-left">
                      <th className="px-4 py-3 font-semibold text-foreground-800">Platform</th>
                      <th className="px-4 py-3 font-semibold text-foreground-800">Item Cost</th>
                      <th className="px-4 py-3 font-semibold text-foreground-800">Service Fee</th>
                      <th className="px-4 py-3 font-semibold text-foreground-800">Shipping</th>
                      <th className="px-4 py-3 font-semibold text-foreground-800">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-background-200/70">
                      <td className="px-4 py-3 font-semibold text-accent-700">
                        <Link to="/" className="hover:underline">ACBuySpreadsheet</Link>
                      </td>
                      <td className="px-4 py-3 text-foreground-700">$200</td>
                      <td className="px-4 py-3 text-foreground-700">$0</td>
                      <td className="px-4 py-3 text-foreground-700">$45</td>
                      <td className="px-4 py-3 font-semibold text-foreground-950">$245</td>
                    </tr>
                    <tr className="border-t border-background-200/70 bg-background-100/50">
                      <td className="px-4 py-3 text-foreground-700">PandaBuy</td>
                      <td className="px-4 py-3 text-foreground-700">$200</td>
                      <td className="px-4 py-3 text-foreground-700">$16</td>
                      <td className="px-4 py-3 text-foreground-700">$62</td>
                      <td className="px-4 py-3 font-semibold text-foreground-950">$278</td>
                    </tr>
                    <tr className="border-t border-background-200/70">
                      <td className="px-4 py-3 text-foreground-700">WeGoBuy</td>
                      <td className="px-4 py-3 text-foreground-700">$200</td>
                      <td className="px-4 py-3 text-foreground-700">$20</td>
                      <td className="px-4 py-3 text-foreground-700">$70</td>
                      <td className="px-4 py-3 font-semibold text-foreground-950">$290</td>
                    </tr>
                    <tr className="border-t border-background-200/70 bg-background-100/50">
                      <td className="px-4 py-3 text-foreground-700">Sugargoo</td>
                      <td className="px-4 py-3 text-foreground-700">$200</td>
                      <td className="px-4 py-3 text-foreground-700">$14</td>
                      <td className="px-4 py-3 text-foreground-700">$58</td>
                      <td className="px-4 py-3 font-semibold text-foreground-950">$272</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Final Thoughts
              </h2>
              <p className="text-foreground-700 text-base leading-relaxed mb-4 font-body">
                {article.conclusion}
              </p>
            </section>

            <div className="rounded-2xl bg-primary-500 p-7 md:p-10 text-center my-12">
              <h3 className="text-background-50 font-heading text-xl md:text-2xl font-bold mb-3">
                Start Saving with ACBuySpreadsheet Today
              </h3>
              <p className="text-background-50/80 text-sm mb-6 max-w-md mx-auto font-body">
                Join thousands of buyers who save 30-40% on every China order. Access the spreadsheet system now.
              </p>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-background-50 hover:bg-background-100 text-primary-700 px-7 py-3 rounded-full text-base font-semibold transition-all cursor-pointer whitespace-nowrap font-label"
              >
                acbuy spreadsheet
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-background-200/70">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-foreground-600 hover:text-primary-600 text-sm font-semibold transition-colors font-label"
              >
                <i className="ri-arrow-left-line"></i>
                Back to ACBuySpreadsheet Home
              </Link>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 text-foreground-600 hover:text-primary-600 text-sm font-semibold transition-colors font-label"
              >
                Visit Main Site
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}